"use client"

import type React from "react"
import { useState } from "react"
import type { SetupData } from "../SetupFlow"
import "./SyncDirection.css"

interface SyncDirectionProps {
  onSubmit: (direction: string) => void
  onBack: () => void
  setupData: SetupData
}

interface SyncDirectionOption {
  id: string
  title: string
  subtitle: string
  description: string
  useCase: string
  icon: React.ReactNode
}

const SyncDirection: React.FC<SyncDirectionProps> = ({ onSubmit, onBack, setupData }) => {
  const [selectedDirection, setSelectedDirection] = useState<string | null>(setupData.syncDirection)

  const syncDirections: SyncDirectionOption[] = [
    {
      id: "bidirectional",
      title: "Bidirectional",
      subtitle: "Data flows both ways — HubSpot ⇄ Salesforce",
      description: "Use this when both platforms should stay fully in sync.",
      useCase: "",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 11l4 4-4 4" />
          <path d="M7 13l-4-4 4-4" />
          <path d="M21 15H3" />
          <path d="M21 9H3" />
        </svg>
      ),
    },
    {
      id: "salesforce-to-hubspot",
      title: "Unidirectional",
      subtitle: "Salesforce → HubSpot",
      description: "Push data from Salesforce to HubSpot only",
      useCase: "Useful if Salesforce is your source of truth",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      ),
    },
    {
      id: "hubspot-to-salesforce",
      title: "Unidirectional",
      subtitle: "HubSpot → Salesforce",
      description: "Push data from HubSpot to Salesforce only.",
      useCase: "Use this if HubSpot is your lead management system",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      ),
    },
  ]

  const handleDirectionSelect = (directionId: string) => {
    setSelectedDirection(directionId)
  }

  const handleSubmit = () => {
    if (selectedDirection) {
      onSubmit(selectedDirection)
    }
  }

  const handleCustomizeSettings = () => {
    console.log("Navigate to sync settings")
  }

  return (
    <div className="sync-direction">
      <div className="sync-direction-header">
        <h1>Select Your Sync Direction</h1>
        <p>Define how your data should flow between Salesforce and HubSpot.</p>
        <p>Choose the option that fits your business need.</p>
      </div>

      <div className="direction-options">
        {syncDirections.map((direction) => (
          <div
            key={direction.id}
            className={`direction-card ${selectedDirection === direction.id ? "selected" : ""}`}
            onClick={() => handleDirectionSelect(direction.id)}
          >
            <div className="direction-icon">{direction.icon}</div>

            <div className="direction-content">
              <h3 className="direction-title">{direction.title}</h3>
              <div className="direction-subtitle">{direction.subtitle}</div>
              <p className="direction-description">{direction.description}</p>
              {direction.useCase && <p className="direction-use-case">{direction.useCase}</p>}
            </div>

            <div className="direction-radio">
              <input
                type="radio"
                name="syncDirection"
                value={direction.id}
                checked={selectedDirection === direction.id}
                onChange={() => handleDirectionSelect(direction.id)}
                className="radio-input"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="customize-section">
        <button className="customize-link" onClick={handleCustomizeSettings}>
          Customize your sync Settings
        </button>
        <p className="settings-note">You can update this later in Settings</p>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={onBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleSubmit} disabled={!selectedDirection}>
          Continue
        </button>
      </div>
    </div>
  )
}

export default SyncDirection
